import React, { useState, useEffect } from 'react';
import { Input, Select, Button } from '../components/ui';
import DateInput from './DateInput';
import SearchableSelect from './SearchableSelect';
import CommentSection from './CommentSection';
import TimeLogSection from './TimeLogSection';
import FilePreviewModal from './FilePreviewModal';
import IconButton from './IconButton';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';


export default function TaskForm({ task, onSubmit, onCancel }) {
  const [isEditing, setIsEditing] = useState(!task); // Edit mode only for new tasks
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [form, setForm] = useState({
    title: task?.title || '',
    type: task?.type || '',
    description: task?.description || '',
    service_id: task?.service_id || '',
    client_id: task?.client_id || task?.client?.id || '',
    l1_reviewer: task?.l1_reviewer || task?.l1_reviewer_user?.id || '',
    l2_reviewer: task?.l2_reviewer || task?.l2_reviewer_user?.id || '',
    status_id: task?.status_id || 2,
    assigned_to: task?.assigned_to || '',
    estimated_time: task?.estimated_time || '',
    is_payment_done: typeof task?.is_payment_done === 'boolean' ? task.is_payment_done : (task?.is_payment_done ? true : false),
    start_date: task?.start_date ? task.start_date.split('T')[0] : '',
    end_date: task?.end_date ? task.end_date.split('T')[0] : '',
    fee: task?.fee || '',
    category: task?.category || ''
  });
  const { user,isAdmin } = useAuth();

  const [services, setServices] = useState([]);
  const [showAddService, setShowAddService] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [addingService, setAddingService] = useState(false);
  const [addServiceError, setAddServiceError] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ company: '', name: '', email: '', phone: '' });
  const [addingClient, setAddingClient] = useState(false);
  const [addClientError, setAddClientError] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [taskDocuments, setTaskDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [statusChangeMessage, setStatusChangeMessage] = useState({ show: false, type: 'success', message: '' });

  // const statuses = [
  //   { value: 'backlog', label: 'Backlog' },
  //   { value: 'todo', label: 'To Do' },
  //   { value: 'inprogress', label: 'In Progress' },
  //   { value: 'approval pending', label: 'Approval Pending' },
  //   { value: 'done', label: 'Completed' }
  // ];

  useEffect(() => {
    const loadFormData = async () => {
      try {
        const [
          servicesRes,
          usersRes,
          statusesRes,
          clients,
          documentsRes
        ] = await Promise.all([
          api.get('/services'),
          api.get('/users'),
          api.get('/statuses'),
          api.get('/clients'),
          task ? api.get(`/documents/task/${task.id}`) : Promise.resolve({ data: [] })
        ]);

        // console.log('Loaded data:', { services: servicesRes.data, users: usersRes.data, statuses: statusesRes.data, clients: clients.data });

          setServices(Array.isArray(servicesRes.data) ? servicesRes.data : servicesRes.data.data || []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.data || []);
        setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : statusesRes.data.data || []);
        setClients(Array.isArray(clients.data) ? clients.data : clients.data.data || []);
        setTaskDocuments(Array.isArray(documentsRes.data) ? documentsRes.data : documentsRes.data.data || []);
      } catch (err) {
        console.error('Failed to load form data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, []);

  const hasPermission = (moduleName, action) => {
    const perms = user?.permissions || {};
    const key = Object.keys(perms).find(k => k.toLowerCase() === moduleName.toLowerCase());
    return !!(key && perms[key] && perms[key][action]);
  };

  // Convenience flags
  const canCreate = hasPermission('Task', 'create');
  const canUpdate = hasPermission('Task', 'update');
  const canCreateDocument = hasPermission('Document', 'create');
  const canReadDocument = hasPermission('Document', 'read');
  const canDeleteDocument = hasPermission('Document', 'delete');

  const validateForm = () => {
    const errors = {};

    if (!form.title?.trim()) {
      errors.title = 'Title is required';
    }

    if (!form.type?.trim()) {
      errors.type = 'Type is required';
    }

    if (!form.description?.trim()) {
      errors.description = 'Description is required';
    }

    if (!form.service_id) {
      errors.service_id = 'Service is required';
    }

    if (!form.assigned_to) {
      errors.assigned_to = 'Assign To is required';
    }

    if (!form.estimated_time) {
      errors.estimated_time = 'Estimated Time is required';
    } else if (isNaN(form.estimated_time) || form.estimated_time <= 0) {
      errors.estimated_time = 'Estimated Time must be a positive number';
    }

    if (!form.start_date) {
      errors.start_date = 'Start Date is required';
    }

    if (!form.end_date) {
      errors.end_date = 'End Date is required';
    }

    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      errors.end_date = 'End Date must be after Start Date';
    }

    if (isAdmin && !form.fee) {
      errors.fee = 'Fee is required for admin';
    } else if (isAdmin && (isNaN(form.fee) || form.fee < 0)) {
      errors.fee = 'Fee must be a valid number';
    }
    if (!form.category?.trim()) {
      errors.category = 'Category is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the validation errors and try again');
      return;
    }

    if (task) {
      if (!canUpdate) {
        setError('You do not have permission to update tasks');
        return;
      }
    } else {
      if (!canCreate) {
        setError('You do not have permission to create tasks');
        return;
      }
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key !== 'documents') {
        formData.append(key, value);
      }
    });
    
    if (form.documents) {
      form.documents.forEach(file => {
        formData.append('documents', file);
      });
    }

    try {
      await onSubmit(formData);
      // Reset the file input after successful submission
      setForm(prev => ({ ...prev, documents: null }));
    } catch (err) {
      setError(err.message || 'Failed to submit the form');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download document');
    }
  };

  const deleteDocument = async (docId) => {
    if (!canDeleteDocument) {
      setError('You do not have permission to delete documents');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await api.delete(`/documents/${docId}`);
      setTaskDocuments(prev => prev.filter(doc => doc.id !== docId));
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  // Allow status update for existing tasks regardless of role by calling dedicated endpoint
  const handleStatusChange = async (e) => {
    const newStatusId = e.target.value;
    setError(null);
    
    if (!task) return;

    // Validate status transition is allowed
    const newStatus = statuses.find(s => s.id === parseInt(newStatusId));
    if (!newStatus) {
      setStatusChangeMessage({
        show: true,
        type: 'error',
        message: 'Invalid status selected'
      });
      return;
    }

    // Check if this status is in the allowed list
    if (!allowedStatuses.find(s => s.id === parseInt(newStatusId))) {
      const errorMsg = `Cannot transition from "${task.Status?.name}" to "${newStatus.name}".`;
      setStatusChangeMessage({
        show: true,
        type: 'error',
        message: errorMsg
      });
      setError(errorMsg);
      return;
    }

    setForm(prev => ({ ...prev, status_id: newStatusId }));
    
    try {
      const { data } = await api.patch(`/tasks/${task.id}/status`, { status_id: newStatusId });
      // update local form with returned values
      setForm(prev => ({ ...prev, status_id: data.status_id, completed_at: data.completed_at }));
      
      // Show success popup
      setStatusChangeMessage({
        show: true,
        type: 'success',
        message: `Status updated to ${newStatus.name} successfully`
      });
      
      // Auto-close popup after 3 seconds
      setTimeout(() => {
        setStatusChangeMessage({ show: false, type: 'success', message: '' });
      }, 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update status';
      setStatusChangeMessage({
        show: true,
        type: 'error',
        message: errorMsg
      });
      setError(errorMsg);
    }
  };

  if (loading) return <div>Loading...</div>;

  // Get allowed statuses based on role-based rules
  const getAllowedStatuses = () => {
    if (!task) return statuses; // Allow all statuses when creating new task
    
    const currentStatusName = (task.Status?.name || '').toLowerCase().trim();
    const userId = user?.id;
    const userRole = (user?.role_obj?.name || user?.role || '').toLowerCase();
    
    const isAssignee = Number(task.assigned_to) === Number(userId) || Number(task.assigned_to_user?.id) === Number(userId);
    const isL1Reviewer = Number(task.l1_reviewer) === Number(userId) || Number(task.l1_reviewer_user?.id) === Number(userId);
    const isL2Reviewer = Number(task.l2_reviewer) === Number(userId) || Number(task.l2_reviewer_user?.id) === Number(userId);
    const isAdmin = userRole === 'admin';

    console.log('Debug - Current status:', currentStatusName);
    console.log('Debug - User ID:', userId);
    console.log('Debug - Is Assignee:', isAssignee);
    console.log('Debug - Is L1 Reviewer:', isL1Reviewer);
    console.log('Debug - Is L2 Reviewer:', isL2Reviewer);
    console.log('Debug - Is Admin:', isAdmin);

    // Admin can change to any status
    if (isAdmin) {
      return statuses;
    }

    // Assignee: Can only change to In Progress or L1 Review/Reviewer
    if (isAssignee && !isL1Reviewer && !isL2Reviewer) {
      console.log('Debug - Assignee only, filtering to In Progress and L1 Review');
      console.log('Debug - Available statuses:', statuses.map(s => s.name));
      const assigneeStatuses = statuses.filter(s => {
        const statusName = s.name.toLowerCase().trim();
        // Match "In Progress", "In Review", "L1 Review", "L1 Reviewer", "L1 Approval", "To Do"
        return statusName.includes('inprogress') || 
               statusName.includes('l1 - approval pending') ||
               statusName.includes('to do') ||
               statusName.includes('minutes to task - conversion pending') ||
               (statusName.includes('l1') && (statusName.includes('review') || statusName.includes('approval')));
      });
      console.log('Debug - Filtered assignee statuses:', assigneeStatuses.map(s => s.name));
      return assigneeStatuses;
    }

    // L1 Reviewer: Can only change to L2 Review/Reviewer statuses
    if (isL1Reviewer) {
      console.log('Debug - L1 Reviewer, filtering to L2 Review statuses');
      const l2ReviewStatuses = statuses.filter(s => {
        const statusName = s.name.toLowerCase();
        return statusName.includes('l2') && (statusName.includes('review') || statusName.includes('reviewer'));
      });
      console.log('Debug - Filtered L2 Review statuses:', l2ReviewStatuses.map(s => s.name));
      return l2ReviewStatuses;
    }

    // L2 Reviewer: Can only change to L2 Approval statuses
    if (isL2Reviewer) {
      console.log('Debug - L2 Reviewer, filtering to L2 Approval only');
      const l2ApprovalStatuses = statuses.filter(s => {
        const statusName = s.name.toLowerCase();
        return statusName.includes('l2') && (statusName.includes('approval') || statusName.includes('approver'));
      });
      console.log('Debug - Filtered L2 Approval statuses:', l2ApprovalStatuses.map(s => s.name));
      return l2ApprovalStatuses;
    }

    // If status is "L1 Approval Pending", can only move to L2 Approval statuses
    if (currentStatusName.includes('l1') && currentStatusName.includes('approval')) {
      console.log('Debug - In L1 Approval, filtering to L2 Approval only');
      const l2ApprovalStatuses = statuses.filter(s => {
        const statusName = s.name.toLowerCase();
        return statusName.includes('l2') && (statusName.includes('approval') || statusName.includes('approver'));
      });
      console.log('Debug - Filtered L2 Approval statuses:', l2ApprovalStatuses.map(s => s.name));
      return l2ApprovalStatuses;
    }

    // If status is "L2 Approval Pending", only admin or L2 reviewer/approver can mark Done
    if (currentStatusName.includes('l2') && currentStatusName.includes('approval')) {
      console.log('Debug - In L2 Approval, checking if can mark Done');
      return statuses.filter(s => s.name.toLowerCase() === 'done');
    }

    // For all other statuses, show all available statuses
    return statuses;
  };

  const allowedStatuses = getAllowedStatuses();

  // If user is trying to create a new task but lacks permission, show a message
  if (!task && !canCreate) return (
    <div style={{ padding: 20, backgroundColor: '#fff6f5', border: '1px solid #ffd7d0', borderRadius: 6 }}>
      You do not have permission to create tasks.
    </div>
  );

  const renderReadOnlyField = (label, value) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ color: '#6B778C', fontSize: '14px', marginBottom: '4px' }}>{label}</div>
      <div style={{ 
        padding: '8px', 
        backgroundColor: '#F4F5F7', 
        borderRadius: '4px',
        minHeight: label === 'Description' ? '100px' : 'auto',
        whiteSpace: 'pre-wrap'
      }}>
        {value || '-'}
      </div>
    </div>
  );

  return (
    <>
    {/* Quick status changer (always available for existing tasks) */}
    {task && (
      <div style={{ marginBottom: 12, backgroundColor: '#e3f2fd', padding: '8px', borderRadius: '4px' }}>
        <label style={{ marginRight: 8, color: '#6B778C' }}>
          Change Status:
          <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
            (Current: {task.Status?.name})
          </span>
        </label>
        <select value={form.status_id} onChange={handleStatusChange}>
          <option value="">-- Select status --</option>
          {allowedStatuses.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        {allowedStatuses.length < statuses.length && (
          <div style={{ fontSize: '12px', color: '#d32f2f', marginTop: '4px' }}>
            ℹ️ Only allowed transitions shown based on workflow
          </div>
        )}
      </div>
    )}
    {task && hasPermission('Task', 'update') &&!isEditing && (
      // make it both are in one row and button should be aligned right and background color light grey
      <div style={{ color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#203c58ff', padding: '8px', borderRadius: '4px' }}>
        <h3> View Task Details</h3>
        <div >
          <Button onClick={() => setIsEditing(true)}>
            Edit Task
          </Button>
        </div>
      </div>
    )}
    {task && isEditing && (
      // make it both are in one row and button should be aligned right and background color light grey
      <div style={{ color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#203c58ff', padding: '8px', borderRadius: '4px' }}>
        <h3> Edit Task Details</h3>
      </div>
    )}

    <form onSubmit={handleSubmit} style={{ pointerEvents: (!task || isEditing) ? 'auto' : 'none', opacity: (!task || isEditing) ? 1 : 0.8 }}>
     
     <div className="grid-two-col">
        <Input
          label="Type"
          placeholder="Enter task type - GST/DSC/ROC"
          value={form.type}
          onChange={e => setForm({ ...form, type: e.target.value })}
          required
          error={formErrors.type}
        />
        {/* category is the dorpdown with two options WO/Minute */}
        <Select
          label="Category"
          value={form.category}
          required
          options={[
            { value: 'WO', label: 'WO' },
            { value: 'Minutes', label: 'Minutes' }
          ]}
          onChange={e => setForm({ ...form, category: e.target.value })}  
        />
      </div>
      <Input
          label="Title"
          placeholder="Enter task title"
          value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })}
          required
          error={formErrors.title}
        />    
      <div style={{ marginBottom: 10 }}>
        <label>
          Description
          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
          :
          <textarea
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px',
              marginTop: '4px',
              border: formErrors.description ? '2px solid #de350b' : '1px solid #ccc',
              borderRadius: '4px',
              minHeight: '100px',
              backgroundColor: formErrors.description ? '#ffebe6' : 'white'
            }}
            required
          />
        </label>
        {formErrors.description && (
          <div style={{ color: '#de350b', fontSize: '12px', marginTop: '4px' }}>
            {formErrors.description}
          </div>
        )}
      </div>

      <SearchableSelect
        label="Service"
        value={form.service_id}
        options={[...services.map(s => ({ value: s.id, label: s.name })), { value: '__add_service__', label: 'Add New Service...' }]}
        onChange={e => {
          const val = e?.target?.value;
          if (val === '__add_service__') {
            setShowAddService(true);
            setNewServiceName('');
            setAddServiceError(null);
          } else {
            setForm({ ...form, service_id: val });
          }
        }}
        required
        error={formErrors.service_id}
        placeholder="Search and select a service..."
      />

      {showAddService && (
        <div style={{ marginBottom: 10, padding: 12, border: '1px solid #e6e6e6', borderRadius: 6, background: '#b0d0feff' }}>
          <div style={{ marginBottom: 8, color: '#42526e', fontWeight: 600 }}>Add New Service</div>
          <input
            type="text"
            value={newServiceName}
            onChange={e => setNewServiceName(e.target.value)}
            placeholder="Service name"
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
          />
          {addServiceError && <div style={{ color: '#de350b', fontSize: 13, marginBottom: 8 }}>{addServiceError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              onClick={async () => {
                setAddServiceError(null);
                const name = (newServiceName || '').trim();
                if (!name) {
                  setAddServiceError('Service name is required');
                  return;
                }
                try {
                  setAddingService(true);
                  const res = await api.post('/services', { name });
                  // Normalize returned service
                  const created = res.data && (res.data.data || res.data) ? (res.data.data || res.data) : null;
                  const newService = created && created.id ? created : (res.data ? res.data : { id: created?.id, name });
                  // Prepend to services list
                  setServices(prev => [newService, ...prev.filter(s => Number(s.id) !== Number(newService.id))]);
                  // Select the new service
                  setForm(prev => ({ ...prev, service_id: newService.id }));
                  setShowAddService(false);
                } catch (err) {
                  console.error('Failed to add service', err);
                  setAddServiceError(err.response?.data?.error || err.message || 'Failed to create service');
                } finally {
                  setAddingService(false);
                }
              }}
              disabled={addingService}
            >
              {addingService ? 'Adding...' : 'Add Service'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setShowAddService(false); setAddServiceError(null); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <SearchableSelect
        label="Client"
        value={form.client_id}
        options={[...clients.map(c => ({ value: c.id, label: c.company })), { value: '__add_client__', label: 'Add new client...' }]}
        onChange={e => {
          const val = e?.target?.value;
          if (val === '__add_client__') {
            setShowAddClient(true);
            setNewClient({ company: '', name: '', email: '', phone: '' });
            setAddClientError(null);
          } else {
            setForm({ ...form, client_id: val });
          }
        }}
        placeholder="Search and select a client..."
      />

      {showAddClient && (
        <div style={{ marginBottom: 10, padding: 12, border: '1px solid #e6e6e6', borderRadius: 6, background: '#b0d0feff' }}>
          <div style={{ marginBottom: 8, color: '#42526e', fontWeight: 600 }}>Add New Client</div>
          <input
            type="text"
            value={newClient.company}
            onChange={e => setNewClient(prev => ({ ...prev, company: e.target.value }))}
            placeholder="Company"
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
          />
          <input
            type="text"
            value={newClient.name}
            onChange={e => setNewClient(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Contact / Nick Name"
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
          />
          <input
            type="email"
            value={newClient.email}
            onChange={e => setNewClient(prev => ({ ...prev, email: e.target.value }))}
            placeholder="Email (optional)"
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
          />
          <input
            type="tel"
            value={newClient.phone}
            onChange={e => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="Phone (optional)"
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc', marginBottom: 8 }}
          />
          {addClientError && <div style={{ color: '#de350b', fontSize: 13, marginBottom: 8 }}>{addClientError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              onClick={async () => {
                setAddClientError(null);
                const company = (newClient.company || '').trim();
                const name = (newClient.name || '').trim();
                if (!company && !name) {
                  setAddClientError('Company or Name is required');
                  return;
                }
                try {
                  setAddingClient(true);
                  const payload = { company: company || name, name: name || company, email: newClient.email || null, phone: newClient.phone || null };
                  const res = await api.post('/clients', payload);
                  const created = res.data && (res.data.data || res.data) ? (res.data.data || res.data) : null;
                  const newC = created && created.id ? created : (res.data ? res.data : { id: created?.id, company: payload.company, name: payload.name });
                  setClients(prev => [newC, ...prev.filter(c => Number(c.id) !== Number(newC.id))]);
                  setForm(prev => ({ ...prev, client_id: newC.id }));
                  setShowAddClient(false);
                } catch (err) {
                  console.error('Failed to add client', err);
                  setAddClientError(err.response?.data?.error || err.message || 'Failed to create client');
                } finally {
                  setAddingClient(false);
                }
              }}
              disabled={addingClient}
            >
              {addingClient ? 'Adding...' : 'Add Client'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setShowAddClient(false); setAddClientError(null); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="grid-two-col">
        <Select
          label="L1 Reviewer"
          value={form.l1_reviewer}
          options={users.map(u => ({ value: u.id, label: u.firstName + ' ' + u.lastName }))}
          onChange={e => setForm({ ...form, l1_reviewer: e.target.value })}
        />

        <Select
          label="L2 Reviewer"
          value={form.l2_reviewer}
          options={users.map(u => ({ value: u.id, label: u.firstName + ' ' + u.lastName }))}
          onChange={e => setForm({ ...form, l2_reviewer: e.target.value })}
        />
      </div>

      <div className="grid-two-col">
        {/* <Select
          label="Status"
          value={form.status_id}
          options={statuses.map(s => ({ value: s.id, label: s.name }))}
          onChange={e => setForm({ ...form, status_id: e.target.value })}
          required
        /> */}

        <Select
          label="Assign To"
          value={form.assigned_to}
          options={users.map(u => ({ value: u.id, label: u.firstName + ' ' + u.lastName }))}
          onChange={e => setForm({ ...form, assigned_to: e.target.value })}
          required
          error={formErrors.assigned_to}
        />
        <Input
          label="Estimated Time (hours)"
          type="number"
          value={form.estimated_time}
          onChange={e => setForm({ ...form, estimated_time: e.target.value })}
          required
          error={formErrors.estimated_time}
        />
      </div>
      <div className="grid-two-col">
        
        {isAdmin && (
          <>
            <Input
              label="Fee"
              type="number"
              value={form.fee}
              onChange={e => setForm({ ...form, fee: e.target.value })}
              required
              error={formErrors.fee}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.is_payment_done}
                  onChange={e => setForm({ ...form, is_payment_done: e.target.checked })}
                />
                <span>Payment Done</span>
              </label>
            </div>
          </>
        )}
      </div>
      <div className="grid-two-col">
        <DateInput
          label="Start Date"
          value={form.start_date}
          onChange={val => setForm({ ...form, start_date: val })}
          required
          error={formErrors.start_date}
        />

        <DateInput
          label="End Date"
          value={form.end_date}
          onChange={val => setForm({ ...form, end_date: val })}
          required
          error={formErrors.end_date}
        />
      </div>

      {error && (
        <div className="error-message" style={{
          color: '#de350b',
          backgroundColor: '#ffebe6',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {task && taskDocuments.length > 0 && (
        <div className="documents-section" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>Task Documents</h3>
          <div className="document-list" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px'
          }}>
            {taskDocuments.map(doc => (
              <div key={doc.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
                backgroundColor: 'white',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <span 
                  style={{ color: '#42526e', cursor: 'pointer' }}
                  onClick={() => setSelectedDocument(doc)}
                >
                  {doc.name}
                </span>
                <div>
                  {canReadDocument && (
                    <IconButton title="Download document" onClick={() => downloadDocument(doc)} style={{ marginRight: '8px' }}>
                      <svg viewBox="0 0 24 24" fill="none"><path d="M12 3v12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 11l4 4 4-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 21H3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </IconButton>
                  )}
                  {isEditing && canDeleteDocument && (
                    <IconButton title="Delete document" onClick={() => deleteDocument(doc.id)}>
                      <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedDocument && (
        <FilePreviewModal
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
          onDownload={canReadDocument ? () => downloadDocument(selectedDocument) : undefined}
          onDelete={(isEditing && canDeleteDocument) ? () => {
            deleteDocument(selectedDocument.id);
            setSelectedDocument(null);
          } : undefined}
        />
      )}

      {isEditing && (canCreateDocument || canUpdate) && (
        <div className="file-upload" style={{ marginBottom: '20px' }}>
          <label className="file-label" style={{
            display: 'block',
            marginBottom: '8px',
            color: '#42526e'
          }}>
            Upload New Documents:
            <input
              type="file"
              multiple
              name="documents"
              onChange={(e) => {
                if (!canCreateDocument && !canUpdate) {
                  setError('You do not have permission to upload documents');
                  return;
                }
                const files = Array.from(e.target.files);
                setForm(prev => ({
                  ...prev,
                  documents: files
                }));
              }}
              style={{
                display: 'block',
                marginTop: '8px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                width: '100%'
              }}
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
            />
          </label>
          {form.documents && form.documents.length > 0 && (
            <div className="selected-files" style={{
              fontSize: '14px',
              color: '#6b778c',
              marginTop: '8px'
            }}>
              Selected files: {form.documents.map(f => f.name).join(', ')}
            </div>
          )}
        </div>
      )}

      {isEditing && (
        <div style={{ marginTop: 20 }}>
          {(task ? canUpdate : canCreate) ? (
            <Button type="submit" style={{ marginRight: 10 }}>
              {task ? 'Update' : 'Create'} Task
            </Button>
          ) : (
            <div style={{ color: '#de350b', marginBottom: 8 }}>
              You do not have permission to {task ? 'update' : 'create'} tasks.
            </div>
          )}

          {task && (
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setIsEditing(false)} 
              style={{ marginRight: 10 }}
            >
              Cancel Edit
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </div>
      )}
    </form>

    {/* Move CommentSection and TimeLogs outside the form */}
    {task && (
      <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
        <TimeLogSection taskId={task.id} />
        <CommentSection taskId={task.id} />
      </div>
    )}

    {/* Status Change Popup */}
    {statusChangeMessage.show && (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: statusChangeMessage.type === 'success' ? '#DFFCF0' : '#FFEBE6',
        color: statusChangeMessage.type === 'success' ? '#216E4E' : '#DE350B',
        padding: '16px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 9999,
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <div>
          {statusChangeMessage.type === 'success' ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <span>{statusChangeMessage.message}</span>
        <button
          onClick={() => setStatusChangeMessage({ show: false, type: 'success', message: '' })}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '0'
          }}
        >
          ✕
        </button>
      </div>
    )}

    <style jsx>{`
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `}</style>
    </>
  );
}